import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const VerifySuccess = () => {
  const { user } = useAuth();
  const [isProcessing, setIsProcessing] = useState(true);
  const [hasSetup, setHasSetup] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsProcessing(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const setupAccount = async () => {
      if (!user || hasSetup) return;

      try {
        setHasSetup(true);
        setIsProcessing(true);

        // Supabase Auth is the authoritative email-verification source. The
        // database trigger creates the account/business; no browser-writable
        // confirmation flag is needed here.
      } catch (error) {
        console.error("Error setting up account:", error);
        toast.error("There was a problem setting up your business account.");
      } finally {
        setIsProcessing(false);
      }
    };

    if (user && !hasSetup) {
      setupAccount();
    }
  }, [user, hasSetup]);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-lg shadow-xl border-border/50 text-center animate-in fade-in zoom-in duration-500">
        <CardHeader className="pt-10 pb-6">
          <div className="mx-auto w-24 h-24 rounded-full flex items-center justify-center mb-6 ring-8 bg-primary/10 ring-primary/5">
            {isProcessing ? (
              <Loader2 className="w-12 h-12 text-primary animate-spin" />
            ) : (
              <CheckCircle2 className="w-12 h-12 text-primary" />
            )}
          </div>
          <CardTitle className="text-3xl font-heading font-bold tracking-tight text-foreground">
            {isProcessing ? "Setting up your account..." : "Email Confirmed!"}
          </CardTitle>
          <CardDescription className="text-base mt-2">
            {isProcessing 
              ? "We're finalizing your account details. Please wait a moment."
              : "Your email has been successfully verified. You are now ready to access the Dibs platform."}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-8 pb-10 text-sm text-muted-foreground leading-relaxed">
          {isProcessing ? (
            <div className="flex justify-center py-4">
              <Loader2 className="w-6 h-6 animate-spin text-primary/50" />
            </div>
          ) : (
            <div className="space-y-6">
              <p className="font-medium text-foreground text-base">You can now close this window and return to the login page to enter your dashboard.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default VerifySuccess;
